import { cx } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ComponentType, ReactNode, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import type { Actions } from "@/models";
import BgtButton from "../BgtButton/BgtButton";
import { BgtText } from "../BgtText/BgtText";

interface Props extends ComponentPropsWithoutRef<"div"> {
	hide?: boolean;
	title?: string;
	icon?: ComponentType<SVGProps<SVGSVGElement>>;
	actions?: Actions[];
	description?: string;
	/** Rendered in the title row, opposite the heading - a control, not an action. */
	headerRight?: ReactNode;
}

export const BgtCard = (props: Props) => {
	const { children, className, hide, title, icon: Icon, actions = [], description, headerRight, ...rest } = props;
	const { t } = useTranslation();

	if (hide) {
		return null;
	}

	return (
		<div className={cx("bg-primary/10 border border-primary/20 rounded-lg p-4 flex flex-col", className)} {...rest}>
			{title && (
				<div className="mb-4">
					<div className="flex justify-between items-start w-full">
						<h2 className="text-white tracking-wide flex items-center gap-2">
							{Icon && <Icon className="size-6 text-primary" />}
							{title}
						</h2>
						<div className="flex flex-row items-center gap-2">
							{headerRight}
							{actions.map((action, index) => (
								<BgtButton
									key={typeof action.content === "string" ? action.content : `action-${index}`}
									variant={action.variant}
									size="2"
									onClick={action.onClick}
								>
									{typeof action.content === "string" ? t(action.content) : action.content}
								</BgtButton>
							))}
						</div>
					</div>
					<BgtText size="2" color="white" opacity={50} className="mb-4">
						{description}
					</BgtText>
				</div>
			)}
			{children}
		</div>
	);
};
